import React, { useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Switch,
    IconButton,
    Menu,
    MenuItem,
    Chip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Tooltip,
    useTheme
} from '@mui/material';
// import { useNavigate } from 'react-router-dom';

// Icons
import MoreVertIcon from '@mui/icons-material/MoreVert';
// import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArticleIcon from '@mui/icons-material/Article';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import EventIcon from '@mui/icons-material/Event';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RepeatIcon from '@mui/icons-material/Repeat';
import TimerIcon from '@mui/icons-material/Timer';

const AlertCard = ({ alert, onToggle, onDelete, onViewDetails }) => {
    // const navigate = useNavigate();
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Alert type info mapping
    const alertTypeInfo = {
        priceAlert: {
            title: 'Price Alert',
            icon: <TrendingUpIcon fontSize="small" />,
            color: '#4CAF50' // green
        },
        newsAlert: {
            title: 'News Alert',
            icon: <ArticleIcon fontSize="small" />,
            color: '#2196F3' // blue
        },
        congressTrade: {
            title: 'Congressional Trading Alert',
            icon: <AccountBalanceIcon fontSize="small" />,
            color: '#9C27B0' // purple
        },
        insiderTrade: {
            title: 'Insider Trading Alert',
            icon: <BusinessIcon fontSize="small" />,
            color: '#FF9800' // orange
        },
        marketEvent: {
            title: 'Market Event Alert',
            icon: <EventIcon fontSize="small" />,
            color: '#F44336' // red
        }
    };

    // Get alert type info
    const typeInfo = alertTypeInfo[alert.type] || {
        title: 'Alert',
        icon: <NotificationsIcon fontSize="small" />,
        color: '#757575' // gray
    };

    // Format creation date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Menu handlers
    const handleMenuOpen = (event) => {
        event.stopPropagation(); // Stop the click from bubbling to card
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (e) => {
        if (e) {
            e.stopPropagation(); // Stop the click from bubbling to card
        }
        setAnchorEl(null);
    };

    // const handleEdit = (e) => {
    //     e.stopPropagation(); // Stop the click from bubbling to card
    //     handleMenuClose();
    //     navigate(`/alerts/edit/${alert._id}`);
    //   };

    const handleDeleteClick = (e) => {
        e.stopPropagation(); // Stop the click from bubbling to card
        handleMenuClose();
        setDeleteDialogOpen(true);
    };

    const handleToggle = (e) => {
        e.stopPropagation(); // Stop the click from bubbling to card
        onToggle(alert._id, alert.enabled);
    };

    const handleDeleteConfirm = (e) => {
        if (e) e.stopPropagation(); // Stop the click from bubbling to card
        onDelete(alert._id);
        setDeleteDialogOpen(false);
    };

    const handleDeleteCancel = (e) => {
        if (e) e.stopPropagation(); // Stop the click from bubbling to card
        setDeleteDialogOpen(false);
    };

    // Handle card click
    const handleCardClick = (e) => {
        // Only trigger if we clicked on the card itself, not its children
        if (onViewDetails) {
            onViewDetails(alert);
        }
    };


    // Generate alert summary based on type
    const getAlertSummary = () => {
        const { type } = alert;
        const conditions = alert.conditions || {};
        const mainFilters = alert.mainFilters || [];

        switch (type) {
            case 'priceAlert':
                return getPriceAlertSummary(conditions, mainFilters);
            case 'congressTrade':
                return getCongressionalTradeSummary(conditions, mainFilters);
            case 'newsAlert':
                return getNewsAlertSummary(conditions, mainFilters);
            case 'insiderTrade':
                return getInsiderTradeSummary(conditions, mainFilters);
            case 'marketEvent':
                return getMarketEventSummary(conditions, mainFilters);
            default:
                return 'Alert details not available';
        }
    };

    // Summary generators for each alert type
    const getPriceAlertSummary = (conditions = {}, mainFilters = []) => {
        const symbols = mainFilters
            .filter(filter => filter.type === 'symbol')
            .map(filter => filter.value);

        if (symbols.length === 0) return 'No stocks selected';

        const symbol = conditions.symbol || symbols[0];
        let summary = '';

        if (conditions.direction === 'above' && conditions.price) {
            summary = `Alert when ${symbol} rises above $${conditions.price}`;
        } else if (conditions.direction === 'below' && conditions.price) {
            summary = `Alert when ${symbol} falls below $${conditions.price}`;
        } else if (conditions.direction === 'up' && conditions.threshold) {
            summary = `Alert when ${symbol} increases by ${conditions.threshold}%`;
        } else if (conditions.direction === 'down' && conditions.threshold) {
            summary = `Alert when ${symbol} decreases by ${conditions.threshold}%`;
        } else {
            summary = `Alert for ${symbol}`;
        }

        if (symbols.length > 1) {
            summary += ` (and ${symbols.length - 1} other stocks)`;
        }

        return summary;
    };

    const getCongressionalTradeSummary = (conditions = {}, mainFilters = []) => {
        const representatives = mainFilters
            .filter(filter => filter.type === 'representative')
            .map(filter => filter.value);

        const symbols = mainFilters
            .filter(filter => filter.type === 'symbol')
            .map(filter => filter.value);

        let summary = '';

        if (representatives.length > 0) {
            if (representatives.length === 1) {
                summary = `Alert for trades by ${representatives[0]}`;
            } else {
                summary = `Alert for trades by ${representatives.length} representatives`;
            }
        }

        if (symbols.length > 0) {
            if (summary) summary += ' for ';
            else summary = 'Alert for trades of ';

            if (symbols.length === 1) {
                summary += symbols[0];
            } else {
                summary += `${symbols.length} stocks`;
            }
        }

        if (conditions.minAmount) {
            summary += ` with minimum value of $${Number(conditions.minAmount).toLocaleString()}`;
        }

        return summary || 'Alert for congressional trades';
    };

    const getNewsAlertSummary = (conditions = {}, mainFilters = []) => {
        const symbols = mainFilters
            .filter(filter => filter.type === 'symbol')
            .map(filter => filter.value);

        const keywords = mainFilters
            .filter(filter => filter.type === 'keyword')
            .map(filter => filter.value);

        let summary = 'Alert for news';

        if (symbols.length > 0) {
            if (symbols.length === 1) {
                summary += ` about ${symbols[0]}`;
            } else {
                summary += ` about ${symbols.length} stocks`;
            }
        }

        if (keywords.length > 0) {
            if (summary !== 'Alert for news') summary += ' with keywords: ';
            else summary += ' containing keywords: ';

            if (keywords.length === 1) {
                summary += `"${keywords[0]}"`;
            } else {
                summary += `${keywords.length} keywords`;
            }
        }

        if (conditions.sentiment) {
            summary += ` with ${conditions.sentiment} sentiment`;
        }

        return summary;
    };

    const getInsiderTradeSummary = (conditions = {}, mainFilters = []) => {
        const symbols = mainFilters
            .filter(filter => filter.type === 'symbol')
            .map(filter => filter.value);

        let summary = 'Alert for insider trades';

        if (symbols.length > 0) {
            if (symbols.length === 1) {
                summary += ` of ${symbols[0]}`;
            } else {
                summary += ` of ${symbols.length} stocks`;
            }
        }

        if (conditions.positions && conditions.positions.length > 0) {
            if (conditions.positions.length === 1) {
                summary += ` by ${conditions.positions[0]}s`;
            } else {
                summary += ` by executives`;
            }
        }

        if (conditions.transactionType) {
            summary += ` (${conditions.transactionType}s only)`;
        }

        return summary;
    };

    const getMarketEventSummary = (conditions = {}, mainFilters = []) => {
        if (!conditions.eventTypes || conditions.eventTypes.length === 0) {
            return 'Alert for market events';
        }

        if (conditions.eventTypes.includes('earnings')) {
            return 'Daily earnings announcements alert';
        }

        const eventTypeNames = {
            'dividends': 'Dividend Announcements',
            'splits': 'Stock Splits',
            'ipo': 'IPO Announcements',
            'economic': 'Economic Data'
        };

        const eventTypes = conditions.eventTypes.map(type => eventTypeNames[type] || type);

        if (eventTypes.length === 1) {
            return `Alert for ${eventTypes[0]}`;
        } else {
            return `Alert for ${eventTypes.length} market event types`;
        }
    };

    return (
        <Card
            sx={{
                mb: 2,
                borderLeft: `4px solid ${typeInfo.color}`,
                transition: 'all 0.2s',
                opacity: alert.enabled ? 1 : 0.7,
                cursor: 'pointer',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                }
            }}
            onClick={handleCardClick}
        >
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                            sx={{
                                mr: 1.5,
                                color: typeInfo.color,
                                display: 'flex'
                            }}
                        >
                            {typeInfo.icon}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            {typeInfo.title}
                        </Typography>
                    </Box>

                    <Box>
                        <Tooltip title={alert.enabled ? "Disable alert" : "Enable alert"}>
                            <Switch
                                checked={alert.enabled}
                                onChange={handleToggle}
                                onClick={(e) => e.stopPropagation()}
                                color="primary"
                                size="small"
                            />
                        </Tooltip>

                        <IconButton
                            size="small"
                            onClick={handleMenuOpen}
                            aria-label="alert actions"
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>

                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            onClick={(e) => e.stopPropagation()} // Also stop clicks within the menu
                        >
                            {/*<MenuItem onClick={handleEdit}>
                                <EditIcon fontSize="small" sx={{ mr: 1 }} />
                                Edit
                            </MenuItem> */}
                            <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                                Delete
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>

                <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                    {alert.name}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {getAlertSummary()}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                            size="small"
                            icon={alert.triggerType === 'once' ? <TimerIcon fontSize="small" /> : <RepeatIcon fontSize="small" />}
                            label={alert.triggerType === 'once' ? "One-time alert" : "Continuous alert"}
                            variant="outlined"
                        />

                        {alert.lastTriggeredAt && (
                            <Chip
                                size="small"
                                label="Triggered"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                        Created {formatDate(alert.createdAt)}
                    </Typography>
                </Box>
            </CardContent>

            {/* Delete confirmation dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{
                    sx: {
                        bgcolor: theme.palette.mode === 'dark' 
                            ? 'rgba(19, 47, 76, 0.4)'
                            : theme.palette.background.paper,
                        backgroundImage: 'none',
                        backdropFilter: theme.palette.mode === 'dark' ? 'blur(10px)' : 'none',
                        border: theme.palette.mode === 'dark'
                            ? '1px solid rgba(255, 255, 255, 0.1)'
                            : '1px solid rgba(0, 0, 0, 0.12)',
                    }
                }}
            >
                <DialogTitle>Delete Alert</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the alert "{alert.name}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default AlertCard;